'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';

type MonochromeContextType = {
  isMonochrome: boolean;
  setMonochrome: (value: boolean) => void;
  toggleMonochrome: () => void;
};

const MonochromeContext = createContext<MonochromeContextType | undefined>(undefined);

const STORAGE_KEY = 'monochrome-mode';

export function MonochromeProvider({ children }: { children: React.ReactNode }) {
  const [isMonochrome, setIsMonochrome] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Load preference from localStorage on mount
  useEffect(() => {
    setIsMounted(true);
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'true') {
      setIsMonochrome(true);
      document.documentElement.classList.add('monochrome');
    }
  }, []);

  const setMonochrome = useCallback((value: boolean) => {
    setIsMonochrome(value);
    localStorage.setItem(STORAGE_KEY, String(value));

    if (value) {
      document.documentElement.classList.add('monochrome');
    } else {
      document.documentElement.classList.remove('monochrome');
    }
  }, []);

  const toggleMonochrome = useCallback(() => {
    setMonochrome(!isMonochrome);
  }, [isMonochrome, setMonochrome]);

  // Prevent hydration mismatch
  if (!isMounted) {
    return (
      <MonochromeContext.Provider value={{ isMonochrome: false, setMonochrome: () => {}, toggleMonochrome: () => {} }}>
        {children}
      </MonochromeContext.Provider>
    );
  }

  return (
    <MonochromeContext.Provider value={{ isMonochrome, setMonochrome, toggleMonochrome }}>
      {children}
    </MonochromeContext.Provider>
  );
}

export function useMonochrome() {
  const context = useContext(MonochromeContext);
  if (context === undefined) {
    throw new Error('useMonochrome must be used within a MonochromeProvider');
  }
  return context;
}
